/**
 * CameraScheduleList — a camera's capture schedules as an editable list.
 *
 * Shared body for both camera-schedule surfaces: the inline panel on a wide
 * camera card and the slide-out schedules blade. Renders the schedule rows
 * (time + days, tap to edit) sorted by time, an add affordance, and an empty
 * state; editing and creation reuse the existing schedule dialog.
 *
 * Contains:
 * - CameraScheduleList — the list + add (default export)
 */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, List as MuiList, Typography } from '@mui/material';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CameraScheduleDialog from '@sophocles/views/sites/components/CameraCards/CameraScheduleDialog';
import CameraScheduleListItem from '@sophocles/views/sites/components/CameraCards/CameraScheduleListItem';

export default function CameraScheduleList({ siteId, camera, canAddSchedules }) {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);

  const schedules = camera?.schedules ?? [];
  const sorted = [...schedules].sort((a, b) => moment(a.time, 'HH:mm:ss').diff(moment(b.time, 'HH:mm:ss')));

  return (
    <>
      {canAddSchedules && camera && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 0.5 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ textTransform: 'none' }}>
            {t('sites.addSchedule', 'Add Schedule')}
          </Button>
        </Box>
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
          {t('sites.noSchedules', 'No schedules')}
        </Typography>
      ) : (
        <MuiList disablePadding>
          {sorted.map(schedule => (
            <CameraScheduleListItem siteId={siteId} camera={camera} schedule={schedule} key={schedule.id} />
          ))}
        </MuiList>
      )}

      {addOpen && camera && (
        <CameraScheduleDialog
          siteId={siteId}
          camera={camera}
          schedule={null}
          open={addOpen}
          closeDialog={() => setAddOpen(false)}
        />
      )}
    </>
  );
}

CameraScheduleList.propTypes = {
  /** Site id — required to route write calls to the odl-app endpoint. */
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Plain-JS camera object (null renders an empty list). */
  camera: PropTypes.object,
  canAddSchedules: PropTypes.bool,
};
